// AuthContext.tsx - Enhanced with Session Management Integration

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  type PropsWithChildren,
} from 'react'
import { Outlet } from 'react-router'

import { authService } from '@/services/authService'
import { authSessionStorageService } from '@/services/sessionStorageService'
import { sessionManager } from '@/services/sessionManager'
import { multiTabSync } from '@/services/multiTabSync'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
  sessionInfo: SessionInfo | null // Added session info
  extendSession: () => Promise<boolean> // Added manual session extension
}

interface SessionInfo {
  timeLeft: number
  sessionAge: number
  lastActivity: number
  isActive: boolean
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  login: async () => false,
  logout: async () => {},
  refreshAuth: async () => {},
  sessionInfo: null,
  extendSession: async () => false,
})

// Debug flag for AuthContext logging
const DEBUG_AUTH_CONTEXT = process.env.NODE_ENV === 'development' && true

export const AuthProvider: React.FC<
  PropsWithChildren<{
    children?: React.ReactNode
  }>
> = ({ children }) => {
  // Initialize state - be more conservative with initial auth state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [authCheckCount, setAuthCheckCount] = useState<number>(0)
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null)

  if (DEBUG_AUTH_CONTEXT) {
    console.log('🔮 AuthProvider render - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'checkCount:', authCheckCount)
  }

  /**
   * Setup session management system when authenticated
   */
  const setupSessionManagement = useCallback(() => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔧 AuthContext: Setting up session management...')
    }

    try {
      // Initialize multi-tab sync first
      multiTabSync.initialize()

      // Initialize session manager (will auto-start activity tracking and warnings)
      sessionManager.initialize()

      // Setup session info updates
      const updateSessionInfo = () => {
        const info = sessionManager.getSessionInfo()
        setSessionInfo(info)
      }

      // Update session info immediately
      updateSessionInfo()

      // Update session info every 30 seconds
      const sessionInfoInterval = setInterval(updateSessionInfo, 30000)

      // Setup session event handlers
      const unsubscribeLogout = sessionManager.onLogout(() => {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🚪 AuthContext: Session manager logout triggered')
        }
        logout()
      })

      // Cleanup function
      return () => {
        clearInterval(sessionInfoInterval)
        unsubscribeLogout()
        sessionManager.cleanup()
        multiTabSync.cleanup()
        setSessionInfo(null)
      }
    } catch (error) {
      console.error('🔧 AuthContext: Error setting up session management:', error)
      return () => {} // Return empty cleanup function
    }
  }, [])

  /**
   * Enhanced authentication check with cross-tab session sync
   */
  const checkAuthStatus = useCallback(async () => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔍 AuthContext: Starting enhanced auth check...')
    }
    
    setIsLoading(true)
    
    try {
      // Get current session data for debugging
      const sessionData = authSessionStorageService.getAllSessionData()
      
      // Check for cross-tab session indicators
      const lastActivity = localStorage.getItem('session_last_activity')
      const sessionTimer = localStorage.getItem('session_timer_start')
      const masterTab = localStorage.getItem('session_master_tab')
      
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔍 AuthContext: Session analysis:', {
          localSession: {
            sessionId: !!sessionData.sessionId,
            loginTime: !!sessionData.loginTime,
            hasKeys: !!(sessionData.publicKey && sessionData.secretKey),
          },
          crossTabSession: {
            lastActivity: !!lastActivity,
            sessionTimer: !!sessionTimer,
            masterTab: !!masterTab,
            activityAge: lastActivity ? Date.now() - parseInt(lastActivity) : null,
            sessionAge: sessionTimer ? Date.now() - parseInt(sessionTimer) : null
          }
        })
      }
      
      // If we have cross-tab session but missing local session data, try to sync
      if ((lastActivity && sessionTimer) && (!sessionData.sessionId || !sessionData.loginTime)) {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔍 AuthContext: Cross-tab session detected, attempting sync...')
        }
        
        // Check if the cross-tab session is still valid (within 30 minutes)
        const sessionAge = Date.now() - parseInt(sessionTimer)
        const isSessionValid = sessionAge < (30 * 60 * 1000) // 30 minutes
        
        if (isSessionValid) {
          if (DEBUG_AUTH_CONTEXT) {
            console.log('🔍 AuthContext: Cross-tab session is valid, requesting sync...')
          }
          
          // Request session sync from master tab
          if (typeof multiTabSync.syncSessionFromMasterTab === 'function') {
            multiTabSync.syncSessionFromMasterTab()
          }
          
          // Give multiTabSync a moment to sync data
          await new Promise(resolve => setTimeout(resolve, 800))
          
          // Re-check session data after sync delay
          const updatedSessionData = authSessionStorageService.getAllSessionData()
          if (updatedSessionData.sessionId && updatedSessionData.loginTime) {
            if (DEBUG_AUTH_CONTEXT) {
              console.log('🔍 AuthContext: Session data synced from other tab')
            }
          }
        } else {
          if (DEBUG_AUTH_CONTEXT) {
            console.log('🔍 AuthContext: Cross-tab session expired, clearing...')
          }
          // Clear expired cross-tab session markers
          localStorage.removeItem('session_last_activity')
          localStorage.removeItem('session_timer_start')
          localStorage.removeItem('session_master_tab')
        }
      }
      
      // Call the auth service to check authentication
      const authenticated = await authService.isAuthenticated()
      
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔍 AuthContext: Auth service result:', authenticated)
      }
      
      setIsAuthenticated(authenticated)
      setAuthCheckCount(prev => prev + 1)
      
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔍 AuthContext: Auth state updated to:', authenticated)
      }
      
    } catch (error) {
      if (DEBUG_AUTH_CONTEXT) {
        console.error('🔍 AuthContext: Auth check error:', error)
      }
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Handle login with proper state management
   */
  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔐 AuthContext: Login attempt initiated for:', username)
    }
    
    setIsLoading(true)
    
    try {
      const success = await authService.login(username, password)
      
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔐 AuthContext: Login service result:', success)
      }
      
      if (success) {
        // Force immediate auth state refresh after successful login
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔐 AuthContext: Login successful, refreshing auth state...')
        }
        
        // Small delay to ensure storage operations complete
        setTimeout(async () => {
          await checkAuthStatus()
        }, 100)
      } else {
        setIsAuthenticated(false)
        setIsLoading(false)
      }
      
      return success
    } catch (error) {
      if (DEBUG_AUTH_CONTEXT) {
        console.error('🔐 AuthContext: Login error:', error)
      }
      setIsAuthenticated(false)
      setIsLoading(false)
      return false
    }
  }, [checkAuthStatus])

  /**
   * Handle logout with state cleanup
   */
  const logout = useCallback(async (): Promise<void> => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🚪 AuthContext: Logout initiated')
    }
    
    setIsLoading(true)
    
    try {
      // Cleanup session management first
      sessionManager.cleanup()
      multiTabSync.cleanup()
      setSessionInfo(null)
      
      // Then perform auth service logout
      await authService.logout()
      setIsAuthenticated(false)
      
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🚪 AuthContext: Logout completed')
      }
    } catch (error) {
      if (DEBUG_AUTH_CONTEXT) {
        console.error('🚪 AuthContext: Logout error:', error)
      }
      // Even if logout fails, clear local state
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Manual auth refresh
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔄 AuthContext: Manual auth refresh requested')
    }
    await checkAuthStatus()
  }, [checkAuthStatus])

  /**
   * Manual session extension
   */
  const extendSession = useCallback(async (): Promise<boolean> => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔄 AuthContext: Manual session extension requested')
    }
    
    try {
      return await sessionManager.extendSession()
    } catch (error) {
      console.error('🔄 AuthContext: Session extension error:', error)
      return false
    }
  }, [])

  /**
   * Setup session management when authenticated
   */
  useEffect(() => {
    let cleanup: (() => void) | undefined

    if (isAuthenticated) {
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔧 AuthContext: User authenticated, setting up session management...')
      }
      cleanup = setupSessionManagement()
    } else {
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔧 AuthContext: User not authenticated, cleaning up session management...')
      }
      // Clean up session management when not authenticated
      sessionManager.cleanup()
      multiTabSync.cleanup()
      setSessionInfo(null)
    }

    return cleanup
  }, [isAuthenticated, setupSessionManagement])

  /**
   * Setup cross-tab authentication sync
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Listen for session changes from other tabs
      if (event.key === 'session_last_activity' || 
          event.key === 'session_timer_start' ||
          event.key === 'session_master_tab') {
        
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔔 AuthContext: Cross-tab session change detected:', event.key)
        }
        
        // Debounce rapid changes
        setTimeout(() => {
          checkAuthStatus()
        }, 300)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [checkAuthStatus])

  /**
   * Initial auth check and auth service callback setup
   */
  useEffect(() => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔮 AuthContext: Setting up auth monitoring')
    }
    
    // Register for auth state changes from the service (if available)
    let unsubscribe: (() => void) | undefined
    
    if (authService.onAuthStateChange) {
      unsubscribe = authService.onAuthStateChange(() => {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔔 AuthContext: Auth state change notification received')
        }
        checkAuthStatus()
      })
    }

    // Initial auth check
    checkAuthStatus()

    // Cleanup
    return () => {
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔮 AuthContext: Cleaning up auth monitoring')
      }
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [checkAuthStatus])

  /**
   * Debug logging for state changes
   */
  useEffect(() => {
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔮 AuthContext: State changed - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'checkCount:', authCheckCount)
    }
  }, [isAuthenticated, isLoading, authCheckCount])

  /**
   * Enhanced initial state check with cross-tab session detection
   */
  useEffect(() => {
    // Quick initial check without async operations
    const sessionData = authSessionStorageService.getAllSessionData()
    const hasValidSession = !!(sessionData.sessionId && sessionData.loginTime)
    
    // Also check for cross-tab session activity
    const lastActivity = localStorage.getItem('session_last_activity')
    const sessionTimer = localStorage.getItem('session_timer_start')
    const hasCrossTabSession = !!(lastActivity && sessionTimer)
    
    if (DEBUG_AUTH_CONTEXT) {
      console.log('🔮 AuthContext: Enhanced initial check:', {
        hasValidSession,
        hasCrossTabSession,
        sessionData: {
          sessionId: !!sessionData.sessionId,
          loginTime: !!sessionData.loginTime
        },
        crossTabData: {
          lastActivity: !!lastActivity,
          sessionTimer: !!sessionTimer
        }
      })
    }
    
    // If we have either local session OR cross-tab session indicators
    if (hasValidSession || hasCrossTabSession) {
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔮 AuthContext: Setting optimistic authenticated state')
      }
      setIsAuthenticated(true)
      
      // If we only have cross-tab indicators, sync the session data
      if (!hasValidSession && hasCrossTabSession) {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔮 AuthContext: Detected cross-tab session, will verify in auth check')
        }
        // The full auth check will validate and sync the session
      }
    }
  }, [])

  /**
   * Periodic auth check (less frequent than before)
   */
  useEffect(() => {
    if (isAuthenticated) {
      if (DEBUG_AUTH_CONTEXT) {
        console.log('🔮 AuthContext: Setting up periodic auth check')
      }
      
      const interval = setInterval(() => {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔮 AuthContext: Periodic auth check')
        }
        checkAuthStatus()
      }, 10 * 60 * 1000) // Check every 10 minutes (less frequent)
      
      return () => {
        if (DEBUG_AUTH_CONTEXT) {
          console.log('🔮 AuthContext: Cleaning up periodic auth check')
        }
        clearInterval(interval)
      }
    }
  }, [isAuthenticated, checkAuthStatus])

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshAuth,
    sessionInfo,
    extendSession,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </AuthContext.Provider>
  )
}

// Export a hook for easier consumption
export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}